import { formatNumber } from '@angular/common';
import { Renderer2 } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Leg, RfqLegSettings } from '@approot/models';
import { Utilities } from '@approot/utilities';
import { numberOfXToUseFn } from '@approot/utilities/schedule-utilities';
import { getStrikeValue } from '../services/leg-spot-strike.service';
import {
  manuallyUpdateFormControlValidity,
  scheduleLegAttributeChange,
  setCalculatedField
} from './leg.utilities';

export function counterNotionalOrPair(
  form: FormGroup,
  leg: Leg,
  index: string = ''
): string {
  // used to decide what to add to the end of notional text box
  if (form.get('quantoCurrency')) {
    return form.get('quantoCurrency').value;
  } else if (form.get('currency')) {
    return form.get('currency').value;
  } else if (form.get('depositCurrency')) {
    return form.get('depositCurrency').value;
  } else if (form.get(`currency${index}`) && index) {
    return form.get(`currency${index}`).value;
  } else {
    return leg.baseCurrency;
  }
}

export function notionalChangeNoStrikeReCalc(
  notionalNumber: string,
  form: FormGroup,
  leg: Leg,
  rfqExecutionSettings: RfqLegSettings,
  element: Element,
  renderer: Renderer2
): void {
  // Continuation of what happens when notional changes without calcuating the strike
  const notionalToAdd = counterNotionalOrPair(form, leg, notionalNumber);

  let textToAdd = ' ' + notionalToAdd;
  if (form.get('schedule')) {
    // we want to add per fixing to the end of the textbox if there is a schedule
    textToAdd = textToAdd + ' per fixing';
  }

  if (leg.productType === 'straddle') {
    textToAdd = textToAdd + ' per leg';
  }

  if (!rfqExecutionSettings.directRfq) {
    Utilities.endOfTextBoxAdder(
      element,
      form.get('notional' + notionalNumber),
      textToAdd,
      !!notionalToAdd,
      'currency',
      renderer
    );
  }
  [
    'schedule',
    'fixedLegSchedule',
    'fixedLegSchedule1',
    'fixedLegSchedule2',
    'floatingLegSchedule'
  ].forEach(scheduleName => {
    if (
      (scheduleName === 'fixedLegSchedule1' && notionalNumber !== '1') ||
      (scheduleName === 'fixedLegSchedule2' && notionalNumber !== '2')
    ) {
      return;
    }
    scheduleLegAttributeChange(
      form,
      leg,
      'notional' + notionalNumber,
      scheduleName,
      'notional'
    );
  });
}

export function counterNotionalAfterValuesSet(
  counterNotionalNumber: string,
  form: FormGroup,
  leg: Leg,
  element: Element,
  renderer: Renderer2
): void {
  if (form.get('counterNotional' + counterNotionalNumber)) {
    Utilities.endOfTextBoxAdder(
      element,
      form.get('counterNotional' + counterNotionalNumber),
      ' ' + leg.quoteCurrency,
      !!leg.quoteCurrency,
      'currency',
      renderer
    );
  }
}

export function onCounterNotionalChange(
  val: string,
  counterNotionalNumber: string,
  form: FormGroup,
  presetsLoaded: boolean,
  leg: Leg,
  rfqExecutionSettings: RfqLegSettings,
  notionalElem: Element,
  counterNotionalElem: Element,
  renderer: Renderer2
) {
  let notionalValue = parseFloat(
    form.get('notional' + counterNotionalNumber).value.replace(/,/g, '')
  );
  // add a currency to end of text box
  const strikeValue = getStrikeValue(
    form.get('strike' + counterNotionalNumber)
  );
  const value = Utilities.formattedNumberToNumber(val);

  if (
    !isNaN(value) &&
    !isNaN(strikeValue) &&
    +strikeValue &&
    (presetsLoaded || !notionalValue)
  ) {
    // if we have a notional currency and a strike multiply these
    form
      .get('notional' + counterNotionalNumber)
      .setValue(setCalculatedField(value, strikeValue, '/'), {
        emitEvent: false
      });
  } else if (
    !isNaN(strikeValue) &&
    strikeValue !== '' &&
    (presetsLoaded || !notionalValue)
  ) {
    // if strike is empty set notional to also be empty
    form
      .get('notional' + counterNotionalNumber)
      .setValue('', { emitEvent: false });
  } else if (strikeValue === '' && !isNaN(value) && !isNaN(notionalValue)) {
    // if strike is empty and there is a notional and notional currency
    // set the strike by dividing
    form
      .get('strike' + counterNotionalNumber)
      .setValue(setCalculatedField(value, notionalValue, '/'));
  }

  // update the validity of the notional
  notionalChangeNoStrikeReCalc(
    counterNotionalNumber,
    form,
    leg,
    rfqExecutionSettings,
    notionalElem,
    renderer
  );
  counterNotionalAfterValuesSet(
    counterNotionalNumber,
    form,
    leg,
    counterNotionalElem,
    renderer
  );
  manuallyUpdateFormControlValidity('notional' + counterNotionalNumber, form);

  if (
    counterNotionalNumber === '1' &&
    form.get('counterNotional2') &&
    presetsLoaded
  ) {
    // if there is a counterNotional1 changes set counterNotional2 to the same value
    form.get('counterNotional2').setValue(val);
  }
}

export function notionalShortLongOnCrossChange(
  notionalKey: string,
  form: FormGroup,
  leg: Leg,
  elem: Element,
  renderer: Renderer2
): void {
  if (form.get(notionalKey)) {
    // if there is a notional, we need to decide where the currency we will add to the text box will come from
    // e.g base currency, quote currency or currency drop box
    if (form.get('currency')) {
      form
        .get(notionalKey)
        .setValue(
          Utilities.notionalCurrencyRemover(form.get(notionalKey).value)
        );
    } else {
      const notionalToAdd = counterNotionalOrPair(form, leg);
      // add the chosen currency to the textBox
      Utilities.endOfTextBoxAdder(
        elem,
        form.get(notionalKey),
        ' ' + notionalToAdd,
        !!notionalToAdd,
        'currency',
        renderer
      );
    }
  }
}

export function updateTotalNotional(
  form: FormGroup,
  leg: Leg,
  presetsLoaded: boolean
): void {
  const totalNotionalCtrl = form.get('totalNotional');
  const nbCtrl = form.get(numberOfXToUseFn(form));
  const notionalCtrl = form.get('notional');
  if (!totalNotionalCtrl || !nbCtrl || !notionalCtrl || !presetsLoaded) {
    return;
  }
  totalNotionalFormatted(form, leg);
}

export function calculateTotalNotionalFromSchedule(
  form: FormGroup,
  leg: Leg
): void {
  const totalNotionalCtrl = form.get('totalNotional');
  if (!totalNotionalCtrl) {
    return;
  }
  const schedule = form.get('schedule').value as Record<string, any>[];
  const totalNotional = schedule.reduce(
    (accumulator, currentValue) =>
      accumulator + Utilities.formattedNumberToNumber(currentValue.notional),
    0
  );
  totalNotionalCtrl.setValue(
    `${formatNumber(totalNotional, 'en-GB', '1.2-2')} ${counterNotionalOrPair(
      form,
      leg
    )}`,
    { emitEvent: false }
  );
}

export function calculateNotionalFromTotalNotional(form: FormGroup): void {
  const nbCtrl = form.get(numberOfXToUseFn(form));
  const totalNotionalCtrl = form.get('totalNotional');
  const notionalCtrl = form.get('notional');
  const totalNotional = Utilities.formattedNumberToNumber(
    totalNotionalCtrl.value
  );
  notionalCtrl.setValue(
    formatNumber(totalNotional / +nbCtrl.value, 'en-GB', '1.2-2'),
    { emitEvent: false }
  );
}

function totalNotionalFormatted(
  form: FormGroup,
  leg: Leg,
  onBlur = false
): void {
  const nbCtrl = form.get(numberOfXToUseFn(form));
  const nb = +nbCtrl.value;
  const notionalCtrl = form.get('notional');
  const totalNotionalCtrl = form.get('totalNotional');
  const notional = +Utilities.formattedNumberToNumber(notionalCtrl.value);
  let updatedNotional = +((notional * nb) / nb).toFixed(2);
  let totalNotional = updatedNotional * nb;

  if (
    !Utilities.isNotBlank(notionalCtrl.value) ||
    !Utilities.isNotBlank(nbCtrl.value)
  ) {
    totalNotionalCtrl.setValue('', { emitEvent: false });
    return;
  }

  if (+nbCtrl.value === 0) {
    updatedNotional = 0;
    totalNotional = 0;
  }

  const formattedTotalNotional = `${formatNumber(
    totalNotional,
    'en-GB',
    '1.2-2'
  )} ${counterNotionalOrPair(form, leg)}`;

  const formattedNotional = formatNumber(updatedNotional, 'en-GB', '1.2-2');
  totalNotionalCtrl.setValue(formattedTotalNotional, { emitEvent: false });
  if (onBlur) {
    notionalCtrl.setValue(
      `${formattedNotional} ${counterNotionalOrPair(form, leg)} per fixing`,
      { emitEvent: false }
    );
  }

  (form.get('schedule') as FormArray)?.controls.forEach(row => {
    row.get('notional')?.setValue(formattedNotional);
  });
}

export function onTotalNotionalBlur(form: FormGroup, leg: Leg): void {
  const notionalCtrl = form.get('notional');
  const totalNotionalCtrl = form.get('totalNotional');
  const totalNotional = +Utilities.formattedNumberToNumber(
    totalNotionalCtrl.value
  );
  const nbCtrl = form.get(numberOfXToUseFn(form));
  const nb = +nbCtrl.value;

  if (totalNotionalCtrl.value === '') {
    notionalCtrl?.setValue('', { emitEvent: false });
    return;
  }

  const fixingNotional = (totalNotional / nb).toFixed(2);

  const notional = `${formatNumber(
    +fixingNotional,
    'en-GB',
    '1.2-2'
  )} ${counterNotionalOrPair(form, leg)} per fixing`;

  const formattedTotalNotional = `${formatNumber(
    +fixingNotional * nb,
    'en-GB',
    '1.2-2'
  )} ${counterNotionalOrPair(form, leg)}`;

  totalNotionalCtrl.setValue(formattedTotalNotional, { emitEvent: false });
  notionalCtrl.setValue(notional, { emitEvent: false });
  (form.get('schedule') as FormArray)?.controls.forEach(row => {
    row
      .get('notional')
      ?.setValue(formatNumber(+fixingNotional, 'en-GB', '1.2-2'));
  });
}
